/**
 * Ligar e desligar as notificações, no atendimento (#536).
 *
 * Duas escolhas independentes — ver `preferencias.ts`:
 *
 *   - **Cards na tela**: a pilha no canto superior direito;
 *   - **Avisos fora da tela**: o balão do sistema, com o CRM em outra aba.
 *
 * ── A permissão do navegador fica visível ────────────────────────────────
 *
 * O aviso fora da tela depende de duas coisas: a pessoa QUERER (a preferência,
 * que é nossa) e o navegador DEIXAR (a permissão, que é dele). O interruptor
 * mostra ligado só quando as duas valem — um "ligado" que não avisa nada é o
 * pior estado possível, porque ensina a desconfiar do controle.
 *
 * Ligar pela primeira vez pede a permissão (é um gesto, e o navegador exige
 * gesto). Bloqueado no navegador, o interruptor fica desabilitado e diz onde
 * liberar: daqui não dá para desfazer um bloqueio, e fingir que dá seria pior.
 *
 * ── O som (13/10/2026) ───────────────────────────────────────────────────
 *
 * Embaixo dos interruptores, a pessoa escolhe o som da mensagem. Escolher toca
 * uma amostra na hora: é o jeito de comparar sem esperar mensagem chegar, e o
 * clique ainda libera o áudio da página (ver `som-do-sino.ts`).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useApi } from '@/shared/api/consultas';
import { Interruptor, Segmentado } from '@/shared/ui';
import {
  COMO_LIBERAR_O_AVISO,
  pedirPermissaoDoAviso,
  usePermissaoDoAviso,
} from '../model/aviso-do-sistema';
import { desligarPush, ligarPush, situacaoDoPush, type SituacaoDoPush } from '../lib/push';
import { definirPreferencia, usePreferenciasDeNotificacao } from '../model/preferencias';
import {
  definirSom,
  ouvirSom,
  SONS,
  somEscolhido,
  type SomDaNotificacao,
} from '../lib/som-do-sino';
import './controle-de-notificacoes.css';

export function ControleDeNotificacoes({
  id,
  aberto = true,
}: {
  id?: string;
  /**
   * Fechado, o painel continua montado — é o que deixa ele SUBIR ao fechar, em
   * vez de sumir de uma vez — mas inerte: sem foco, sem clique, fora do que o
   * leitor de tela lê.
   */
  aberto?: boolean;
}): ReactNode {
  const preferencias = usePreferenciasDeNotificacao();
  const permissao = usePermissaoDoAviso();
  const [som, definirSomNaTela] = useState<SomDaNotificacao>(somEscolhido);

  const escolherSom = (escolhido: SomDaNotificacao): void => {
    definirSom(escolhido);
    definirSomNaTela(escolhido);
    ouvirSom(escolhido);
  };
  const push = usePush(permissao);

  const foraDaTelaLigado = preferencias.foraDaTela && permissao === 'granted';

  const alternarForaDaTela = (): void => {
    if (foraDaTelaLigado) {
      definirPreferencia('foraDaTela', false);
      return;
    }
    definirPreferencia('foraDaTela', true);
    if (permissao === 'default') void pedirPermissaoDoAviso();
  };

  const motivoDoBloqueio =
    permissao === 'denied'
      ? `O navegador bloqueou os avisos deste site. ${COMO_LIBERAR_O_AVISO}`
      : permissao === 'indisponivel'
        ? 'Este navegador não mostra avisos do sistema.'
        : undefined;

  return (
    <div
      id={id}
      className="controle-de-notificacoes"
      data-aberto={aberto ? 'sim' : undefined}
      inert={!aberto}
    >
      <div className="controle-de-notificacoes__conteudo" role="group" aria-label="Notificações">
        <Interruptor
          rotulo="Cards na tela"
          ligado={preferencias.naTela}
          aoAlternar={() => definirPreferencia('naTela', !preferencias.naTela)}
          descricao="O card no canto superior direito, em qualquer tela, quando um cliente escreve."
        />
        <Interruptor
          rotulo="Avisos fora da tela"
          ligado={foraDaTelaLigado}
          aoAlternar={alternarForaDaTela}
          desabilitado={motivoDoBloqueio !== undefined}
          {...(motivoDoBloqueio ? { motivo: motivoDoBloqueio } : {})}
          descricao={
            permissao === 'default'
              ? 'O aviso do computador, com nome e mensagem, quando o CRM está em outra aba. ' +
                'Ao ligar, o navegador pede permissão.'
              : 'O aviso do computador, com nome e mensagem, quando o CRM está em outra aba.'
          }
        />
        <Interruptor
          rotulo="Avisar com o CRM fechado"
          ligado={push.situacao === 'ligado'}
          aoAlternar={push.alternar}
          desabilitado={push.ocupado || MOTIVO_DO_PUSH[push.situacao ?? 'desligado'] !== undefined}
          {...(push.situacao && MOTIVO_DO_PUSH[push.situacao]
            ? { motivo: MOTIVO_DO_PUSH[push.situacao] }
            : {})}
          descricao={
            'O aviso do computador mesmo sem nenhuma aba do CRM aberta. Diz só "Nova mensagem", ' +
            'a não ser que a organização tenha liberado mostrar o nome e o começo da mensagem. ' +
            'Vale neste navegador, e sair do CRM desliga.'
          }
        />
        <div className="controle-de-notificacoes__som">
          <p className="controle-de-notificacoes__titulo-do-som">Som da mensagem</p>
          <Segmentado
            como="grupo"
            aparencia="pilulas"
            rotulo="Som da mensagem"
            opcoes={SONS.map((opcao) => ({ valor: opcao.chave, rotulo: opcao.nome }))}
            escolhida={som}
            aoEscolher={escolherSom}
          />
          <p className="controle-de-notificacoes__descricao-do-som">
            Toca quando um cliente escreve. Escolher um som toca uma amostra. Vale neste navegador.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Quando o interruptor do push não pode mudar, e o porquê — dito na própria linha. */
const MOTIVO_DO_PUSH: Readonly<Partial<Record<SituacaoDoPush, string>>> = {
  indisponivel: 'Este navegador não recebe avisos com o CRM fechado.',
  'sem-servidor': 'O servidor ainda não tem os avisos com o CRM fechado configurados.',
  bloqueado: `O navegador bloqueou os avisos deste site. ${COMO_LIBERAR_O_AVISO}`,
};

/**
 * A situação do push NESTE navegador, lida da inscrição de verdade, e o gesto
 * de ligar e desligar. Relê quando a permissão muda — bloquear no navegador
 * precisa aparecer aqui sem recarregar.
 */
function usePush(permissao: string): {
  situacao: SituacaoDoPush | null;
  ocupado: boolean;
  alternar: () => void;
} {
  const api = useApi();
  const [situacao, definirSituacao] = useState<SituacaoDoPush | null>(null);
  const [ocupado, definirOcupado] = useState(false);

  useEffect(() => {
    if (!api) return;
    let cancelado = false;
    void situacaoDoPush(api)
      .then((atual) => {
        if (!cancelado) definirSituacao(atual);
      })
      .catch(() => {
        if (!cancelado) definirSituacao('sem-servidor');
      });
    return () => {
      cancelado = true;
    };
  }, [api, permissao]);

  const alternar = (): void => {
    if (!api || ocupado) return;
    definirOcupado(true);
    const acao =
      situacao === 'ligado'
        ? desligarPush(api).then((): SituacaoDoPush => 'desligado')
        : ligarPush(api);
    void acao
      .then(definirSituacao)
      .catch(() => situacaoDoPush(api).then(definirSituacao))
      .catch(() => undefined)
      .finally(() => definirOcupado(false));
  };

  return { situacao, ocupado, alternar };
}
